import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Card } from 'react-bootstrap';
import { Key, Plus, Trash, Copy, CheckCircle } from 'react-bootstrap-icons';

function BcmApiKeys(prop) {
    const [apiKeys, setApiKeys] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedKey, setSelectedKey] = useState(null);
    const [newKey, setNewKey] = useState({ name: '', description: '' });
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });
    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);

    useEffect(() => {
        loadApiKeys();
    }, []);

    const loadApiKeys = () => {
        setLoading(true);
        window.post('find_bcm_api_keys', {}, res => {
            if (res.data && res.data.records) {
                setApiKeys(res.data.records);
            }
            setLoading(false);
        });
    };

    const showAlert = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    };

    const handleCreateKey = () => {
        if (!newKey.name.trim()) {
            showAlert('danger', 'Please enter a name for the API key');
            return;
        }

        setLoading(true);
        window.post('create_bcm_api_key', newKey, res => {
            setLoading(false);
            if (res.data && res.data.status === 1) {
                setNewlyCreatedKey({
                    api_key: res.data.api_key,
                    name: res.data.name,
                    created_at: res.data.created_at
                });
                setShowCreateModal(false);
                setNewKey({ name: '', description: '' });
                loadApiKeys();
            } else {
                showAlert('danger', res.data?.value || 'Failed to create API key');
            }
        });
    };

    const handleDeleteKey = (keyId) => {
        setSelectedKey(keyId);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        setLoading(true);
        window.post('delete_bcm_api_key', { keyId: selectedKey }, res => {
            setLoading(false);
            if (res.data && res.data.status === 1) {
                showAlert('success', 'Assessment API key deleted successfully!');
                setShowDeleteModal(false);
                loadApiKeys();
            } else {
                showAlert('danger', res.data?.value || 'Failed to delete API key');
            }
        });
    };

    const closeNewKeyModal = () => {
        setNewlyCreatedKey(null);
    };

    const copyToClipboard = (text) => {
        // Fallback for older browsers or if navigator.clipboard is unavailable
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(() => {
                showAlert('success', 'API key copied to clipboard!');
            }).catch(() => {
                showAlert('danger', 'Failed to copy to clipboard');
            });
        } else {
            // Fallback: create a temporary textarea
            try {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                // Prevent scrolling to bottom
                textarea.style.position = 'fixed';
                textarea.style.top = 0;
                textarea.style.left = 0;
                textarea.style.width = '2em';
                textarea.style.height = '2em';
                textarea.style.padding = 0;
                textarea.style.border = 'none';
                textarea.style.outline = 'none';
                textarea.style.boxShadow = 'none';
                textarea.style.background = 'transparent';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textarea);
                if (successful) {
                    showAlert('success', 'API key copied to clipboard!');
                } else {
                    showAlert('danger', 'Failed to copy to clipboard');
                }
            } catch (err) {
                showAlert('danger', 'Failed to copy to clipboard');
            }
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="maincontent">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2><Key className="me-2" />Assessment API Keys</h2>
                    <p className="text-muted">Manage API keys for Assessment API integration</p>
                </div>
                <Button 
                    variant="primary" 
                    onClick={() => setShowCreateModal(true)}
                    disabled={loading}
                >
                    <Plus className="me-2" />
                    Create New API Key
                </Button>
            </div>

            {alert.show && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false, type: '', message: '' })}>
                    {alert.message}
                </Alert>
            )}

            <Card>
                <Card.Header>
                    <h5 className="mb-0">Your Assessment API keys</h5>
                </Card.Header>
                <Card.Body>
                    {loading && apiKeys.length === 0 ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : apiKeys.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <Key size={48} className="mb-3" />
                            <p>No Assessment API keys found. Create your first API key to get started.</p>
                        </div>
                    ) : (
                        <Table responsive hover>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>API Key</th>
                                    <th>Created</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {apiKeys.map((key) => (
                                    <tr key={key._id}>
                                        <td>
                                            <strong>{key.name}</strong>
                                        </td>
                                        <td>
                                            <span className="text-muted">{key.description || 'No description'}</span>
                                        </td>
                                        <td>
                                            <code>
                                                {key.api_key_obfuscated}
                                            </code>
                                        </td>
                                        <td>
                                            <small className="text-muted">
                                                {formatDate(key.created_at)}
                                            </small>
                                        </td>
                                        <td>
                                            <span className={`badge ${key.active ? 'bg-success' : 'bg-secondary'}`}>
                                                {key.active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleDeleteKey(key._id)}
                                                disabled={loading}
                                            >
                                                <Trash size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Create API Key Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Create Assessment API Key</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Name *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter API key name"
                                value={newKey.name}
                                onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                placeholder="Enter description for this API key"
                                value={newKey.description}
                                onChange={(e) => setNewKey({ ...newKey, description: e.target.value })}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateKey} disabled={loading}>
                        {loading ? 'Creating...' : 'Create API Key'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* New API Key Modal */}
            <Modal show={newlyCreatedKey !== null} onHide={closeNewKeyModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title><CheckCircle className="me-2 text-success" />API Key Created Successfully</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-3">
                        <strong>Important:</strong> This is the only time you will see the full API key. 
                        Please copy it now and store it securely. You won't be able to see it again.
                    </Alert>
                    <div className="mb-3">
                        <label className="form-label"><strong>API Key Name:</strong></label>
                        <p className="text-muted">{newlyCreatedKey?.name}</p>
                    </div>
                    <div className="mb-3">
                        <label className="form-label"><strong>API Key:</strong></label>
                        <div className="d-flex align-items-center">
                            <code className="flex-grow-1 p-2 bg-light border rounded me-2" style={{fontSize: '14px'}}>
                                {newlyCreatedKey?.api_key}
                            </code>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => copyToClipboard(newlyCreatedKey?.api_key)}
                            >
                                <Copy className="me-1" />
                                Copy
                            </Button>
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label"><strong>Created:</strong></label>
                        <p className="text-muted">{newlyCreatedKey?.created_at ? formatDate(newlyCreatedKey.created_at) : ''}</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={closeNewKeyModal}>
                        I've Saved the API Key
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Assessment API Key</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete this Assessment API key?</p>
                    <p className="text-muted">
                        <strong>Warning:</strong> This action cannot be undone. Any applications using this API key will lose access.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={loading}>
                        {loading ? 'Deleting...' : 'Delete API Key'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default BcmApiKeys;
